import { NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { breakpointValue, ifThen, otherwise } from '@cpangular/rxjs';
import { IfHasRenderInSourcesDirective, RenderInService } from 'common-ui';
import { ElementResizeDirective } from 'common-util';
import { NavAnchorDirective } from 'navigation-ui';
import { ProductComponentStore } from 'products-data';
import { Observable } from 'rxjs';
import { ProductSectionComponent } from '../../../common/product-section';
import { PRODUCT_DETAIL_VIEW_COMPONENT_TOKENS } from '../../../types/tokens';
import { ProductDetailHostComponentBase } from '../ProductDetailHostComponentBase';
import { RenderTargetComponents } from './render-targets/RenderTargetComponents';

enum ComponentLayoutSize {
  Small,
  Medium,
  Large,
  ExtraLarge,
}

@Component({
  selector: 'ideal-product-detail-layout',
  imports: [
    NgComponentOutlet,
    RenderTargetComponents,
    ElementResizeDirective,
    NavAnchorDirective,
    ProductSectionComponent,
    IfHasRenderInSourcesDirective,
  ],
  standalone: true,
  templateUrl: './product-detail-layout.component.html',
  styleUrls: ['./product-detail-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RenderInService, ProductComponentStore],
  host: {
    '[attr.layout]': 'ComponentLayoutSize[componentWidth()].toLowerCase()',
    '[style.--header-height]': 'headerHeight() + "px"',
  },
})
export class ProductDetailLayoutComponent extends ProductDetailHostComponentBase {
  private readonly _renderInService = inject(RenderInService);

  /** The title / important-info area only exists when something is registered for one of its targets. */
  protected readonly hasInfo = computed(() =>
    [this.LayoutComponents[this.LayoutTargets.Title], this.LayoutComponents[this.LayoutTargets.ImportantInfo]].some(
      (target) => this._renderInService.getSources(target)().length > 0,
    ),
  );
  private readonly _element = inject(ElementRef<HTMLElement>);

  protected readonly ComponentLayoutSize = ComponentLayoutSize;
  protected readonly headerHeight = signal(0);
  protected readonly componentWidth = toSignal(
    breakpointValue(
      this._element.nativeElement,
      ifThen((size) => size.width < 550, ComponentLayoutSize.Small),
      ifThen((size) => size.width < 800, ComponentLayoutSize.Medium),
      ifThen((size) => size.width < 1000, ComponentLayoutSize.Large),
      otherwise(ComponentLayoutSize.ExtraLarge),
    ) as Observable<ComponentLayoutSize>,
    { initialValue: ComponentLayoutSize.Small },
  );

  protected readonly componentInputs = computed(() => ({
    productId: this.productId(),
  }));

  constructor() {
    super(PRODUCT_DETAIL_VIEW_COMPONENT_TOKENS);
    effect(() => {
      console.log('ProductDetailLayoutComponent - product:', this.product());
    });
  }
}
